import { Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Button, Component_GenericRenderer, LL_H_C, LL_V_L, ModuleFE_Toaster, SimpleListAdapter, TS_Checkbox, TS_DropDown, TS_Input, TS_TextArea } from '@nu-art/thunderstorm-frontend/index';
import { Component_TagEditor, ConfigKeyFE_ComplaintsTagId, ConfigKeyFE_DiseasesTagId, ConfigKeyFE_SourceTagId, ModuleFE_Variable, MultiSelect_Variable, VariableWrapper } from '@app/hcs-frontend';
import { dbObjectToId, exists } from '@nu-art/ts-common';
import { Header_Pathway } from '../../../headers/Header_Pathway.js';
import { Header_WithOptionMenu, ICONS } from '@app/styles';
import { DropDown_DefaultCaret } from '@app/styles/frontend/ui/consts';
import { ComplaintPathwayTypes, Const_ChiefComplaint, Const_DifferentialDiagnosis, Const_OrderSet, Const_Treatment, Const_Triage, DiseasePathwayTypes, pathwayType_SingleState, PathwayTypes, PathwayTypeToLabel, PathwayVersions } from '@app/pathway-shared';
import { basicSyncEventRefresh } from '../../../utils.js';
import { detachPathwayFromParent } from '../../../ui-utils.js';
import { TS_EditableItemComponentProto } from '@nu-art/thunderstorm-frontend/editable-item';
import { ModuleFE_Pathway } from '../../../../../_entity/pathway/index.js';
import { getPathwayHVOrgConflicts } from '../../../../_managers/Manager_Pathway/renderers/shared/hv-org-conflicts.js';
import { HVOrgConflictBanner } from '../../../../_managers/Manager_Pathway/renderers/shared/HVOrgConflictBanner.js';
import { ModuleFE_DiseaseProfile } from '@app/dp-frontend';
import { Editor_ScoreRef } from '@app/dp-frontend/ui/_managers/Manager_DiseaseProfile/renderers/Editor_DiseaseProfile/tabs/Editor_DiseaseProfile_Scores/Editor_DiseaseProfile_Scores';
export class Renderer_PathwayEditor extends TS_EditableItemComponentProto {
    __onPathwayUpdated(...params) {
        const pathwayId = this.state.editable.get('_id');
        if (!pathwayId)
            return;
        basicSyncEventRefresh(pathwayId, params, () => this.props.functions.selectPathway(pathwayId, true), () => this.props.functions.deselectPathway());
    }
    deriveStateFromProps(nextProps, state) {
        state = super.deriveStateFromProps(nextProps, state);
        state.editable.setOnChanged(async () => this.forceUpdate());
        return state;
    }
    // ######################## Logic ########################
    canSave = () => {
        const pathway = this.props.editable.item;
        return !!pathway.variableIds?.length && !!pathway.type;
    };
    onSave = async () => {
        if (!this.canSave())
            return;
        // Mirror the WorkHub guard: block saving while an HV/organization conflict is unresolved.
        if (getPathwayHVOrgConflicts(this.props.editable.item).length)
            return ModuleFE_Toaster.toastError('Resolve the HV / organization conflict before saving.');
        const pathway = this.props.editable;
        pathway.set('version', PathwayVersions[PathwayVersions.length - 1]);
        try {
            if (pathway.item._id) {
                await pathway.save();
                return this.props.functions.selectPathway(pathway.item._id, false);
            }
            const data = await ModuleFE_Pathway._v1.createPathway(pathway.item).executeSync();
            this.props.functions.selectPathway(data.pathway._id, false);
            /*FIXME: Setting timeout here because parent component can't handle the race-condition of getting props and executing the toggleMode callback.
            *  When ComponentSync finally implements a derive queue this can be removed*/
            // setTimeout(() => this.props.toggleMode(), 100);
        }
        catch (err) {
            // The HV/org conflict banner already explains the problem; just prevent the error overlay.
            this.logError('Failed saving pathway', err);
        }
    };
    getAllowedVariableIds = () => {
        const pathway = this.props.editable.item;
        switch (pathway.type) {
            case pathwayType_SingleState:
            case Const_DifferentialDiagnosis:
            case Const_Treatment: {
                const viableVariableIds = ModuleFE_DiseaseProfile.cache.map(dp => dp.variableId);
                const sourceTagId = ConfigKeyFE_SourceTagId.get();
                const tagId = ConfigKeyFE_DiseasesTagId.get();
                return ModuleFE_Variable.cache.filter(variable => {
                    if (!viableVariableIds.includes(variable._id))
                        return false;
                    const sourceGroup = variable.tagIds.find(group => group.key === sourceTagId);
                    return !!sourceGroup?.value.includes(tagId);
                }).map(dbObjectToId);
            }
            case Const_ChiefComplaint:
            case Const_OrderSet:
            case Const_Triage: {
                const sourceTagId = ConfigKeyFE_SourceTagId.get();
                const tagId = ConfigKeyFE_ComplaintsTagId.get();
                return ModuleFE_Variable.cache.filter(variable => {
                    const sourceGroup = variable.tagIds.find(group => group.key === sourceTagId);
                    return !!sourceGroup?.value.includes(tagId);
                }).map(dbObjectToId);
            }
            default:
                return [];
        }
    };
    // ######################## Render ########################
    render() {
        return _jsxs(LL_V_L, { className: 'match_parent', style: { padding: '30px', gap: '10px' }, children: [this.renderHeader(), this.renderHVOrgWarning(), this.renderForm(), this.renderButtons()] });
    }
    // Early UX mirror of the backend HV+org uniqueness rule (backend stays authoritative on save).
    // Non-clickable in the legacy editor (pathways are not navigable from here).
    renderHVOrgWarning = () => _jsx(HVOrgConflictBanner, { conflicts: getPathwayHVOrgConflicts(this.props.editable.item), clickable: false });
    // ######################## Render - Header ########################
    renderHeader = () => {
        const pathway = this.props.editable.item;
        if (!pathway._id)
            return _jsx(Header_WithOptionMenu, { title: 'Add New Pathway' });
        return _jsx(Header_Pathway, { pathway: this.props.editable, allowActions: false, functions: this.props.functions });
    };
    // ######################## Render - Body ########################
    renderForm = () => {
        return _jsxs(LL_V_L, { className: 'pathway__form', children: [this.renderOrgPermission(), this.renderLabelInput(), this.renderTypeDropdown(), this.renderVariableDropdown(), this.renderDescription(), this.renderTagGroups(), this.renderScoreRefs(), this.renderUnderConstructionCheckbox()] });
    };
    renderOrgPermission = () => {
        return _jsx(Component_GenericRenderer, { rendererKey: 'organization-permission-editor', props: {
                item: this.state.editable.item,
                preSelectCallback: async () => detachPathwayFromParent(this.state.editable.get('_id')),
                module: ModuleFE_Pathway,
                direction: 'Vertical'
            } });
    };
    renderUnderConstructionCheckbox = () => {
        return _jsx(_Fragment, { children: _jsx(TS_Checkbox, { checked: this.state.editable.get('underConstruction'), onCheck: async (status) => {
                    this.state.editable.set('underConstruction', status);
                    this.forceUpdate();
                }, children: _jsx("div", { className: 'pathway__form__label', children: "Under Construction" }) }) });
    };
    renderLabelInput = () => {
        return _jsxs(_Fragment, { children: [_jsxs("div", { className: 'pathway__form__label', children: ["Pathway Name", _jsx("sup", { children: "*" })] }), _jsx(TS_Input, { type: 'text', value: this.props.editable.item.label, onChange: value => {
                        this.props.editable.set('label', value);
                        this.forceUpdate();
                    } })] });
    };
    renderTypeDropdown = () => {
        let adapterData = [...PathwayTypes];
        if (exists(this.props.editable.item._id) && exists(this.props.editable.item.type)) {
            switch (this.props.editable.item.type) {
                case Const_DifferentialDiagnosis: // 'differential-diagnosis'
                case Const_Treatment: // 'treatment'
                    adapterData = [...DiseasePathwayTypes];
                    break;
                case Const_ChiefComplaint: // 'chief-complaint'
                case Const_Triage: // 'triage'
                    adapterData = [...ComplaintPathwayTypes];
                    break;
                case pathwayType_SingleState:
                    adapterData = [pathwayType_SingleState];
                    break;
            }
        }
        // Allow interchanging between DD/Treatment, or between CC/Triage, DO NOT allow mixing between these groups if Pathway already exists.
        // This is how we avoid forcing the user to also choose a new variable, because DD/Treatment use Disease variables and CC/Triage use Complaint variables.
        const adapter = SimpleListAdapter(adapterData, type => _jsx(_Fragment, { children: PathwayTypeToLabel[type.item] }));
        // const disabled = !!this.props.editable.item._id;
        return _jsxs(_Fragment, { children: [_jsxs("div", { className: 'pathway__form__label', children: ["Pathway Type", _jsx("sup", { children: "*" })] }), _jsx(TS_DropDown, { adapter: adapter, 
                    // disabled={disabled}
                    selected: this.props.editable.item.type, onSelected: type => {
                        this.props.editable.set('type', type);
                        this.forceUpdate();
                    }, caret: DropDown_DefaultCaret })] });
    };
    renderVariableDropdown = () => {
        const pathway = this.props.editable;
        const pathwayType = pathway.item.type;
        if (!pathwayType)
            return;
        const allowedVariableIds = this.getAllowedVariableIds();
        return _jsxs(_Fragment, { children: [_jsxs("div", { className: 'pathway__form__label', children: [['chief-complaint', 'triage'].includes(pathwayType) ? 'Chief Complaint' : 'Disease', " Name", _jsx("sup", { children: "*" })] }), _jsx(MultiSelect_Variable, { editable: pathway, prop: 'variableIds', itemFilter: id => allowedVariableIds.includes(id) })] });
    };
    renderDescription = () => {
        return _jsxs(_Fragment, { children: [_jsx("div", { className: 'pathway__form__label', children: "Description" }), _jsx(TS_TextArea, { type: 'text', value: this.props.editable.item.description, onChange: value => {
                        this.props.editable.set('description', value);
                        this.forceUpdate();
                    } })] });
    };
    renderTagGroups = () => {
        const uiTagGroups = VariableWrapper.dbTagsToUI(this.state.editable.get('tagGroups'));
        return _jsxs(_Fragment, { children: [_jsx("div", { className: 'pathway__form__label', children: "Tags" }), _jsx(Component_TagEditor, { tags: uiTagGroups, mandatoryRootTag: true, groupTagFilter: () => true, singleTagFilter: (tag, groupTag) => tag.tagIds[0] === groupTag._id, onChange: async () => {
                        const tagGroups = VariableWrapper.getTagsForDB(uiTagGroups);
                        await this.state.editable.updateObj({ tagGroups });
                    } })] });
    };
    renderScoreRefs = () => {
        const editable = this.state.editable.editProp('scoreReferences', []);
        return _jsxs(_Fragment, { children: [_jsx("div", { className: 'pathway__form__label', children: "Scores" }), _jsxs(LL_H_C, { className: 'e__pathway__score-refs', children: [editable.item.map((ref, index) => {
                            const editableScore = editable.editProp(index, {});
                            editableScore.setOnDelete(async () => editable.removeArrayItem(index));
                            return _jsx(Editor_ScoreRef, { editable: editableScore, editMode: true }, index);
                        }), _jsx(ICONS.advisor_avn_add.component, { onClick: () => editable.updateArrayAt({}) })] })] });
    };
    // ######################## Render - Buttons ########################
    renderButtons = () => {
        const buttonText = this.props.editable.item._id ? 'Save' : 'Next';
        return _jsxs(LL_H_C, { className: 'pathway__bottom-buttons', children: [_jsx(Button, { variant: 'secondary', onClick: this.props.onCancel, children: "Cancel" }), _jsx(Button, { variant: 'primary', onClick: this.onSave, className: 'preferred-action', disabled: !this.canSave(), children: buttonText })] });
    };
}
//# sourceMappingURL=Renderer_PathwayEditor.js.map